// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { paginatedResultSchema } from '@/api/paging';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { Sort } from '@/constants/sort';
import { contactSchema } from '@/contact/api/types';
import queryString from 'query-string';
import { z } from 'zod';

export const contactSortPropertySchema = z.enum([
  'name',
  'email',
  'phone',
  'title',
  'customer',
  'lastUpdatedAt',
]);

export type ContactSortProperty = z.infer<typeof contactSortPropertySchema>;

const contactsResponseSchema = z
  .object({
    data: paginatedResultSchema(contactSchema),
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({
    ...response,
    contacts: data.results,
    totalCount: data.totalCount,
  }));

type ContactsResponse = z.infer<typeof contactsResponseSchema>;

export interface ContactFilter {
  customerIds?: string[];
  search?: string;
  email?: string;
  isArchived?: boolean | 'all';
}

interface GetContactsParameters {
  filter?: ContactFilter;
  sort?: Sort<ContactSortProperty>;
  page?: number;
  limit?: number;
}

export async function getContacts(
  parameters: GetContactsParameters = {}
): Promise<ContactsResponse> {
  const url = '/v1/contact';
  const response = await api.get(url, {
    paramsSerializer: (parameters) => queryString.stringify(parameters),
    params: {
      page: parameters.page,
      limit: parameters.limit,
      sortBy: parameters.sort?.property,
      sortOrder: parameters.sort?.direction,
      customerIds: parameters.filter?.customerIds,
      search: parameters.filter?.search,
      email: parameters.filter?.email,
      archived: parameters.filter?.isArchived,
    },
  });

  return parseResponse(contactsResponseSchema, response.data, {
    schemaName: 'Contacts',
    endpoint: url,
  });
}
